import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiVideo, FiSearch, FiLink, FiUser, FiCheck, FiX, FiRefreshCw, FiPlay, FiFileText, FiDownload } from 'react-icons/fi';
import { interviewsAPI, studentsAPI, settingsAPI, zoomAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/Common/StatusBadge';
import Modal from '../../components/Common/Modal';
import FranceInterviewOutcomeModal from '../../components/Common/FranceInterviewOutcomeModal';
import { isFranceCampusName } from '../../utils/campus';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import { toast } from 'react-toastify';
import { getTodayDateInputMin, isDateTimeLocalInFuture, storedDatetimeToLocalInput } from '../../utils/futureDateTime';
import { getBrowserIanaTimezone, resolveDefaultInterviewTimezone } from '../../utils/browserTimezone';
import {
  formatInterviewSlotAvailabilitySuffix,
  canBypassInterviewSlotCapacity
} from '../../utils/interviewSlotAvailabilityLabel';
import {
  getDocumentFileSizeError,
  getInterviewReportFileError,
  INTERVIEW_REPORT_ACCEPT
} from '../../utils/uploadLimits';
import { runTableFilters } from '../../utils/tableFilters';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../context/CountryContext';

const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show', 'student_did_not_join', 'rescheduled'];

/**
 * Human-readable label for an interview status code.
 *
 * Needed because the previous inline formatter used
 * `s.replace('_', ' ')` which only replaces the FIRST underscore — so
 * `student_did_not_join` would render as "Student did_not_join". This
 * helper replaces ALL underscores AND provides a translation hook.
 */
const formatInterviewStatus = (status, t) => {
  if (!status) return '';
  // Collapse "completed_pass" / "completed_fail" to a single "Completed"
  // for user-facing views — this mirrors the existing table rendering.
  if (status === 'completed_pass' || status === 'completed_fail') {
    return t ? t('interviews.completed', { defaultValue: 'Completed' }) : 'Completed';
  }
  const translated = t ? t(`interviews.status.${status}`, { defaultValue: '' }) : '';
  if (translated) return translated;
  // Fallback: "student_did_not_join" → "Student Did Not Join"
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatOutcomeLabel = (outcome, t) => {
  if (!outcome) return '';
  const translated = t(`interviews.outcomes.${outcome}`, { defaultValue: '' });
  if (translated) return translated;
  return String(outcome)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const actionGroupStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'var(--bg-secondary)'
};

const actionBtnBaseStyle = {
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const actionBtnStyle = (variant = 'outline') => {
  if (variant === 'success') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--success)',
      background: 'var(--success-light)',
      color: 'var(--success)'
    };
  }
  if (variant === 'danger') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--danger)',
      background: 'var(--danger-light)',
      color: 'var(--danger)'
    };
  }
  if (variant === 'warning') {
    return {
      ...actionBtnBaseStyle,
      border: '1px solid var(--warning)',
      background: 'var(--warning-light)',
      color: 'var(--warning)',
      fontSize: 9,
      fontWeight: 700
    };
  }
  return actionBtnBaseStyle;
};

const InterviewList = () => {
  const { t } = useTranslation();
  const { activeCountry } = useCountry();
  const showInterviewHostColumn = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const { canPerformAction, user } = useAuth();
  const [reportFile, setReportFile] = useState(null);
  const reportFileInputRef = useRef(null);
  const clearReportFileSelection = useCallback(() => {
    setReportFile(null);
    if (reportFileInputRef.current) reportFileInputRef.current.value = '';
  }, []);
  const [reportBusy, setReportBusy] = useState(false);
  const [outcomeDialog, setOutcomeDialog] = useState(null);
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [students, setStudents] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const [filters, setFilters] = useState({ 
    search: '', 
    studentId: '', 
    status: '', 
    startDate: '', 
    endDate: '' 
  });
  const [showModal, setShowModal] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [slotDate, setSlotDate] = useState(getTodayDateInputMin());
  const [slotId, setSlotId] = useState('');
  const [desiredSlotTime, setDesiredSlotTime] = useState('');
  const [modalCampusId, setModalCampusId] = useState(null);
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({
    student_id: '',
    scheduled_at: '',
    timezone: getBrowserIanaTimezone(),
    interview_link: '',
    remarks: ''
  });

  const passedStudentIds = useMemo(() => {
    const set = new Set();
    for (const i of interviews || []) {
      if (String(i?.outcome || '').toLowerCase() === 'pass') {
        const sid = i?.student_id;
        if (sid !== undefined && sid !== null && sid !== '') set.add(String(sid));
      }
    }
    return set;
  }, [interviews]);

  const selectableStudents = useMemo(() => {
    if (editingInterview) return students || [];
    return (students || []).filter((s) => !passedStudentIds.has(String(s.student_id)));
  }, [students, passedStudentIds, editingInterview]);

  const editingFranceCampus = useMemo(() => {
    if (!editingInterview) return false;
    return isFranceCampusName(
      editingInterview.campus_name ||
        (students || []).find((s) => String(s.student_id) === String(editingInterview.student_id))?.campus_name
    );
  }, [editingInterview, students]);

  const scheduledAtFromSlot = (dateStr, hhmm) => {
    if (!dateStr || !hhmm) return '';
    return `${String(dateStr).slice(0, 10)}T${String(hhmm).slice(0, 5)}`;
  };

  const canUpdateInterviews = canPerformAction('interviews', 'update');
  const canDeleteInterviews = canPerformAction('interviews', 'delete');
  const isSalesExecutive = String(user?.role || '').toLowerCase() === 'sales_executive';
  // Reschedule is intentionally available to every authenticated user — the
  // backend enforces this via a dedicated reschedule-only bypass on
  // PUT /api/interviews/:id.  Edit / delete continue to honour the normal
  // `interviews.update` / `interviews.delete` permissions below.
  const canRescheduleInterviews = !!user;
  const selectedStudentCampusId = useMemo(() => {
    if (modalCampusId) return modalCampusId;
    const sid = form?.student_id || editingInterview?.student_id;
    if (!sid) return null;
    const st = (students || []).find((s) => String(s.student_id) === String(sid));
    return st?.campus_id || null;
  }, [modalCampusId, form?.student_id, editingInterview?.student_id, students]);

  const allowedStudentIds = useMemo(() => {
    if (!isSalesExecutive) return null;
    const set = new Set((students || []).map((s) => String(s.student_id)));
    return set;
  }, [isSalesExecutive, students]);

  const interviewColumns = useMemo(() => [
    { key: 'student', label: t('interviews.studentLabel') },
    { key: 'student_language', label: t('interviews.tableStudentLanguage') },
    ...(showInterviewHostColumn
      ? [{ key: 'interview_host', label: t('interviews.tableInterviewHost') }]
      : []),
    { key: 'campus_name', label: t('interviews.tableCampus') },
    { key: 'scheduled_at', label: t('interviews.tableScheduledAt') },
    { key: 'timezone', label: t('interviews.tableTimezone') },
    { key: 'link', label: t('interviews.tableLink') },
    { key: 'status', label: t('interviews.tableStatus') },
    { key: 'outcome', label: t('interviews.tableOutcome') },
    { key: 'interview_comment', label: t('interviews.tableComment') },
    { key: 'report', label: t('interviews.tableReport') },
    { key: 'created_by', label: t('interviews.tableCreatedBy') },
    { key: 'remarks', label: t('interviews.remarks', 'Remarks'), defaultVisible: false },
    { key: 'zoom_duration_minutes', label: t('interviews.zoomDuration', 'Zoom Duration (min)'), defaultVisible: false },
    { key: 'updated_by_name', label: t('interviews.updatedBy', 'Updated By'), defaultVisible: false },
    { key: 'interview_mode', label: t('interviews.mode', 'Mode'), defaultVisible: false },
    { key: 'actions', label: t('common.actions'), locked: true },
  ], [t, showInterviewHostColumn]);

  const { isVisible, toggleColumn, resetToDefault, columns: intColDefs } = useColumnPreferences('interviews', interviewColumns);
  const columnFilterDefs = useMemo(() => {
    const timezoneOptions = Array.from(new Set((interviews || []).map((i) => String(i.timezone || '')).filter(Boolean)));
    const campusOptions = Array.from(new Set((interviews || []).map((i) => String(i.campus_name || '')).filter(Boolean)));
    const createdByOptions = Array.from(new Set((interviews || []).map((i) => String(i.created_by_name || '')).filter(Boolean)));
    const updatedByOptions = Array.from(new Set((interviews || []).map((i) => String(i.updated_by_name || '')).filter(Boolean)));
    const modeOptions = Array.from(new Set((interviews || []).map((i) => String(i.interview_mode || '')).filter(Boolean)));
    return [
      { key: 'student', label: t('interviews.studentLabel'), field: 'student_full_name', type: 'text' },
      { key: 'student_language', label: t('interviews.tableStudentLanguage'), field: 'student_language', type: 'multi', options: [{ value: 'ENGLISH', label: t('studentForm.languageEnglish') }, { value: 'FRENCH', label: t('studentForm.languageFrench') }] },
      { key: 'interview_host', label: t('interviews.tableInterviewHost'), field: 'interview_host_name', type: 'text' },
      { key: 'campus_name', label: t('interviews.tableCampus'), field: 'campus_name', type: 'multi', options: campusOptions.map((v) => ({ value: v, label: v })) },
      { key: 'scheduled_at', label: t('interviews.tableScheduledAt'), field: 'scheduled_at', type: 'date' },
      { key: 'timezone', label: t('interviews.tableTimezone'), field: 'timezone', type: 'multi', options: timezoneOptions.map((v) => ({ value: v, label: v })) },
      { key: 'status', label: t('interviews.tableStatus'), field: 'status', type: 'multi', options: INTERVIEW_STATUSES.map((s) => ({ value: s, label: formatInterviewStatus(s, t) })) },
      { key: 'outcome', label: t('interviews.tableOutcome'), field: 'outcome', type: 'multi', options: [{ value: 'pass', label: formatOutcomeLabel('pass', t) }, { value: 'fail', label: formatOutcomeLabel('fail', t) }, { value: 'pending', label: formatOutcomeLabel('pending', t) }, { value: 'student_did_not_join', label: formatOutcomeLabel('student_did_not_join', t) }] },
      { key: 'interview_comment', label: t('interviews.tableComment'), field: 'interview_comment', type: 'text' },
      { key: 'created_by', label: t('interviews.tableCreatedBy'), field: 'created_by_name', type: 'multi', options: createdByOptions.map((v) => ({ value: v, label: v })) },
      { key: 'remarks', label: t('interviews.remarks', 'Remarks'), field: 'remarks', type: 'text' },
      { key: 'zoom_duration_minutes', label: t('interviews.zoomDuration', 'Zoom Duration (min)'), field: 'zoom_duration_minutes', type: 'text' },
      { key: 'updated_by_name', label: t('interviews.updatedBy', 'Updated By'), field: 'updated_by_name', type: 'multi', options: updatedByOptions.map((v) => ({ value: v, label: v })) },
      { key: 'interview_mode', label: t('interviews.mode', 'Mode'), field: 'interview_mode', type: 'multi', options: modeOptions.map((v) => ({ value: v, label: v })) }
    ];
  }, [interviews, t]);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const normalizedInterviews = useMemo(
    () =>
      (interviews || []).map((i) => ({
        ...i,
        student_full_name: `${i.title || ''} ${i.first_name || ''} ${i.last_name || ''} ${i.student_ref || ''}`.trim()
      })),
    [interviews]
  );
  const filteredInterviews = useMemo(
    () =>
      runTableFilters(
        normalizedInterviews,
        tableFilters,
        tableFilters,
        [],
        columnFilterDefs
      ),
    [normalizedInterviews, tableFilters, columnFilterDefs]
  );

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.studentId) params.studentId = filters.studentId;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      
      const res = await interviewsAPI.getAll(params);
      let interviewsData = res.data || [];
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        interviewsData = interviewsData.filter(i => 
          `${i.first_name} ${i.last_name}`.toLowerCase().includes(searchLower) ||
          i.student_ref?.toLowerCase().includes(searchLower) ||
          i.remarks?.toLowerCase().includes(searchLower) ||
          String(i.interview_comment || '').toLowerCase().includes(searchLower)
        );
      }

      if (allowedStudentIds) {
        interviewsData = interviewsData.filter((i) => allowedStudentIds.has(String(i?.student_id)));
      }
      
      setInterviews(interviewsData);
    } catch (err) {
      toast.error(t('interviews.failedLoad'));
      console.error(err);
    }
    setLoading(false);
  }, [filters, allowedStudentIds, t]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  useEffect(() => {
    studentsAPI.getAll().then(r => {
      const studentsData = r.data.students || r.data || [];
      setStudents(studentsData);
    }).catch(() => {});

    settingsAPI.getInterviewTimezones().then((res) => {
      const activeZones = (res.data || []).filter((tz) => tz.status === 'active');
      setTimezones(activeZones);
      if (activeZones.length > 0) {
        setForm((prev) => ({ ...prev, timezone: resolveDefaultInterviewTimezone(activeZones) }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showModal || !slotDate) return;
    setSlotsLoading(true);
    settingsAPI
      .getInterviewSlotAvailability({
        date: slotDate,
        type: 'interview',
        campus_id: selectedStudentCampusId || undefined
      })
      .then((res) => setSlotOptions(res.data || []))
      .catch(() => setSlotOptions([]))
      .finally(() => setSlotsLoading(false));
  }, [showModal, slotDate, selectedStudentCampusId]);

  useEffect(() => {
    if (!showModal) return;
    const selected = (slotOptions || []).find((s) => String(s.slot_id) === String(slotId));
    const start = selected?.start_time ? String(selected.start_time).slice(0, 5) : '';
    setForm((f) => ({ ...f, scheduled_at: scheduledAtFromSlot(slotDate, start) }));
  }, [showModal, slotDate, slotId, slotOptions]);

  useEffect(() => {
    if (!showModal) return;
    if (slotId) return;
    if (!desiredSlotTime) return;
    const match = (slotOptions || []).find((s) => String(s.start_time || '').slice(0, 5) === String(desiredSlotTime).slice(0, 5));
    if (match?.slot_id != null) setSlotId(String(match.slot_id));
  }, [showModal, slotOptions, desiredSlotTime, slotId]);

  const openInterviewReport = async (interviewId) => {
    try {
      const { data } = await interviewsAPI.getReportUrl(interviewId);
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.message || t('interviews.couldNotOpenReport'));
    }
  };

  const handleSubmit = async () => {
    if (!form.student_id || !form.scheduled_at || !form.timezone) {
      toast.error(t('interviews.fillRequired'));
      return;
    }
    if (reportFile) {
      const reportPickErr = getInterviewReportFileError(reportFile);
      if (reportPickErr) {
        toast.error(reportPickErr);
        return;
      }
    }
    const prevScheduled = editingInterview?.scheduled_at
      ? storedDatetimeToLocalInput(editingInterview.scheduled_at)
      : '';
    const slotChanged = !editingInterview || form.scheduled_at !== prevScheduled;
    if (slotChanged && !isDateTimeLocalInFuture(form.scheduled_at)) {
      toast.error(t('interviews.futureDateRequired'));
      return;
    }
    
    try {
      const interviewData = {
        scheduled_at: form.scheduled_at,
        timezone: form.timezone,
        interview_link: form.interview_link || null,
        remarks: form.remarks || null
      };

      let savedId = editingInterview?.interview_id ?? null;
      const franceEdit =
        editingInterview &&
        isFranceCampusName(
          editingInterview.campus_name ||
            (students || []).find((s) => String(s.student_id) === String(editingInterview.student_id))?.campus_name
        );

      if (editingInterview) {
        if (isRescheduleMode) {
          // Reschedule-only payload: the backend recognises this shape and
          // permits any authenticated user to call it (see
          // `allowRescheduleOrUpdatePermission` in routes/interviews.js).
          // Outcome is already 'pending' (reschedule button only shows then),
          // so we deliberately do NOT include `outcome` here.
          interviewData.status = 'rescheduled';
        }
        await interviewsAPI.update(editingInterview.interview_id, interviewData);
        savedId = editingInterview.interview_id;
        if (!(reportFile && !franceEdit)) {
          toast.success(isRescheduleMode ? t('interviews.rescheduled') : t('interviews.updated'));
        }
      } else {
        const { data } = await interviewsAPI.create(form.student_id, interviewData);
        savedId = data?.interview_id ?? null;
        if (!reportFile) toast.success(t('interviews.scheduled'));
      }

      if (reportFile && savedId && !franceEdit) {
        const fd = new FormData();
        fd.append('file', reportFile);
        try {
          await interviewsAPI.uploadReport(savedId, fd);
          toast.success(
            editingInterview
              ? (isRescheduleMode ? t('interviews.rescheduledReportUploaded') : t('interviews.updatedReportUploaded'))
              : t('interviews.scheduledReportUploaded')
          );
        } catch (upErr) {
          toast.error(upErr.response?.data?.message || t('interviews.savedReportFailed'));
        }
      }
      
      setShowModal(false);
      setEditingInterview(null);
      setIsRescheduleMode(false);
      clearReportFileSelection();
      setForm({
        student_id: '',
        scheduled_at: '',
        timezone: resolveDefaultInterviewTimezone(timezones),
        interview_link: '',
        remarks: ''
      });
      loadInterviews();
    } catch (err) {
      toast.error(err.response?.data?.message || t('interviews.saveFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('interviews.deleteConfirm'))) return;
    try {
      await interviewsAPI.delete(id);
      toast.success(t('interviews.deleted'));
      loadInterviews();
    } catch (err) {
      toast.error(t('interviews.deleteFailed'));
    }
  };

  const handleEdit = (interview) => {
    setIsRescheduleMode(false);
    setEditingInterview(interview);
    const scheduledDateTime = interview.scheduled_at
      ? storedDatetimeToLocalInput(interview.scheduled_at)
      : '';
    const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : getTodayDateInputMin();
    const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
    setSlotDate(initDate);
    setDesiredSlotTime(initTime);
    setSlotId('');
    
    clearReportFileSelection();
    setForm({
      student_id: interview.student_id.toString(),
      scheduled_at: scheduledDateTime,
      timezone: interview.timezone || resolveDefaultInterviewTimezone(timezones),
      interview_link: interview.interview_link || '',
      remarks: interview.remarks || ''
    });
    setShowModal(true);
  };

  const handleMarkOutcome = async (interview, outcome) => {
    if (isFranceCampusName(interview.campus_name)) {
      setOutcomeDialog({ interview, outcome });
      return;
    }
    try {
      await interviewsAPI.update(interview.interview_id, { outcome });
      toast.success(t('interviews.interviewMarkedAs', { outcome: formatOutcomeLabel(outcome, t) }));
      loadInterviews();
    } catch (err) {
      toast.error(err.response?.data?.message || t('interviews.outcomeUpdateFailed'));
    }
  };

  const handleReschedule = async (interview) => {
    let campusId = null;
    try {
      const r = await studentsAPI.getById(interview.student_id);
      campusId = r?.data?.campus_id || null;
    } catch (_) {}
    setModalCampusId(campusId);
    setIsRescheduleMode(true);
    setEditingInterview(interview);
    const scheduledDateTime = interview.scheduled_at
      ? storedDatetimeToLocalInput(interview.scheduled_at)
      : '';
    const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : getTodayDateInputMin();
    const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
    setSlotDate(initDate);
    setDesiredSlotTime(initTime);
    setSlotId('');
    clearReportFileSelection();
    setForm({
      student_id: interview.student_id.toString(),
      scheduled_at: scheduledDateTime,
      timezone: interview.timezone || resolveDefaultInterviewTimezone(timezones),
      interview_link: interview.interview_link || '',
      remarks: interview.remarks || ''
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'var(--primary)',
      completed: 'var(--success)',
      cancelled: 'var(--text-muted)',
      no_show: 'var(--warning)',
      student_did_not_join: 'var(--danger)',
      rescheduled: 'var(--info)'
    };
    return colors[status] || 'var(--text-muted)';
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    const s = String(dateTimeStr).trim();
    const datePart = s.slice(0, 10);
    const timePart = s.length >= 16 ? s.slice(11, 16) : '';
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return '-';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthName = months[parseInt(m, 10) - 1] || m;
    return `${parseInt(d, 10)} ${monthName} ${y}${timePart ? ', ' + timePart : ''}`;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{t('interviews.title')}</h1>
          <p>{t('interviews.subtitle')}</p>
        </div>
        {canPerformAction('interviews', 'create') && <button className="btn btn-primary" onClick={() => {
          setEditingInterview(null);
          setIsRescheduleMode(false);
          setSlotDate(getTodayDateInputMin());
          setDesiredSlotTime('');
          clearReportFileSelection();
          setForm({
            student_id: '',
            scheduled_at: '',
            timezone: resolveDefaultInterviewTimezone(timezones),
            interview_link: '',
            remarks: ''
          });
          setShowModal(true);
        }}>
          <FiPlus /> {t('interviews.scheduleBtn')}
        </button>}
      </div>

      <div className="table-container" style={{ marginBottom: 16 }}>
        <div className="table-toolbar">
          <div className="toolbar-left">
            <div className="table-search">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={t('interviews.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div className="filter-bar">
              <select
                value={filters.studentId}
                onChange={(e) => setFilters({ ...filters, studentId: e.target.value })}
              >
                <option value="">{t('interviews.allStudents')}</option>
                {students.map(s => (
                  <option key={s.student_id} value={s.student_id}>
                    {s.student_ref} - {s.title} {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">{t('interviews.allStatuses')}</option>
                {INTERVIEW_STATUSES.map(s => (
                  <option key={s} value={s}>
                    {formatInterviewStatus(s, t)}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />

              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
          <ColumnCustomizer columns={intColDefs} isVisible={isVisible} toggleColumn={toggleColumn} resetToDefault={resetToDefault} />
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : filteredInterviews.length === 0 ? (
        <div className="empty-state">
          <FiVideo size={48} />
          <p>{t('interviews.noInterviews')}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  {isVisible('student') && <UnifiedFilterHeaderCell column={columnFilterMap.student} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student_language') && <UnifiedFilterHeaderCell column={columnFilterMap.student_language} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {showInterviewHostColumn && isVisible('interview_host') && <UnifiedFilterHeaderCell column={columnFilterMap.interview_host} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('campus_name') && <UnifiedFilterHeaderCell column={columnFilterMap.campus_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('scheduled_at') && <UnifiedFilterHeaderCell column={columnFilterMap.scheduled_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('timezone') && <UnifiedFilterHeaderCell column={columnFilterMap.timezone} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('link') && <th>{t('interviews.tableLink')}</th>}
                  {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('outcome') && <UnifiedFilterHeaderCell column={columnFilterMap.outcome} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('interview_comment') && <UnifiedFilterHeaderCell column={columnFilterMap.interview_comment} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('report') && <th>{t('interviews.tableReport')}</th>}
                  {isVisible('created_by') && <UnifiedFilterHeaderCell column={columnFilterMap.created_by} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('remarks') && <UnifiedFilterHeaderCell column={columnFilterMap.remarks} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('zoom_duration_minutes') && <UnifiedFilterHeaderCell column={columnFilterMap.zoom_duration_minutes} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('updated_by_name') && <UnifiedFilterHeaderCell column={columnFilterMap.updated_by_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('interview_mode') && <UnifiedFilterHeaderCell column={columnFilterMap.interview_mode} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {canRescheduleInterviews && <th>{t('common.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredInterviews.map(interview => (
                  <tr key={interview.interview_id}>
                    {isVisible('student') && <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiUser />
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/students/${interview.student_id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/students/${interview.student_id}`);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                          title={t('studentDetail.title')}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--primary)' }}>
                            {interview.title} {interview.first_name} {interview.last_name}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {interview.student_ref}
                          </div>
                        </div>
                      </div>
                    </td>}
                    {isVisible('student_language') && <td style={{ fontSize: 13 }}>
                      {interview.student_language === 'FRENCH'
                        ? t('studentForm.languageFrench')
                        : interview.student_language === 'ENGLISH'
                          ? t('studentForm.languageEnglish')
                          : '—'}
                    </td>}
                    {showInterviewHostColumn && isVisible('interview_host') && (
                      <td style={{ fontSize: 13 }}>{interview.interview_host_name || '—'}</td>
                    )}
                    {isVisible('campus_name') && <td style={{ fontSize: 13 }}>{interview.campus_name || '—'}</td>}
                    {isVisible('scheduled_at') && <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FiCalendar style={{ color: 'var(--text-muted)' }} />
                        <span>{formatDateTime(interview.scheduled_at)}</span>
                      </div>
                    </td>}
                    {isVisible('timezone') && <td>{interview.timezone || resolveDefaultInterviewTimezone(timezones)}</td>}
                    {isVisible('link') && <td>
                      {interview.interview_link ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <a href={interview.interview_link} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}>
                            <FiLink /> {t('interviews.joinLink')}
                          </a>
                          {interview.zoom_meeting_id && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              {interview.zoom_recording_url && (
                                <a href={interview.zoom_recording_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiPlay size={10} /> {t('interviews.recording')}
                                </a>
                              )}
                              {interview.zoom_transcript_url && (
                                <a href={interview.zoom_transcript_url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <FiFileText size={10} /> {t('interviews.transcript')}
                                </a>
                              )}
                              {interview.zoom_duration_minutes && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{interview.zoom_duration_minutes} min</span>
                              )}
                              <button title={t('interviews.syncZoom')} className="btn btn-sm btn-outline" style={{ padding: '2px 6px', fontSize: 11 }}
                                onClick={async () => {
                                  try {
                                    const res = await zoomAPI.sync({ entity_type: 'interview', entity_id: interview.interview_id });
                                    toast.success(t('interviews.zoomSynced'));
                                    if (res.data?.errors?.length) {
                                      toast.warn(Array.isArray(res.data.errors) ? res.data.errors.join(' ') : String(res.data.errors));
                                    }
                                    loadInterviews();
                                  } catch (err) { toast.error(t('interviews.syncFailed')); }
                                }}>
                                <FiRefreshCw size={11} />
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>-</span>
                      )}
                    </td>}
                    {isVisible('status') && <td>
                      <span className="badge" style={{ 
                        background: getStatusColor(interview.status) + '20',
                        color: getStatusColor(interview.status)
                      }}>
                        {formatInterviewStatus(interview.status, t)}
                      </span>
                    </td>}
                    {isVisible('outcome') && <td>
                      {interview.outcome && interview.outcome !== 'pending' ? (
                        <span className="badge" style={{
                          background: interview.outcome === 'pass' ? 'var(--success-light)' : 'var(--danger-light)',
                          color: interview.outcome === 'pass' ? 'var(--success)' : 'var(--danger)'
                        }}>
                          {formatOutcomeLabel(interview.outcome, t)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>-</span>
                      )}
                    </td>}
                    {isVisible('interview_comment') && <td
                      style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}
                      title={interview.interview_comment || ''}
                    >
                      {interview.interview_comment || '-'}
                    </td>}
                    {isVisible('report') && <td>
                      {interview.report_file_path && canPerformAction('interviews', 'read') ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          onClick={() => openInterviewReport(interview.interview_id)}
                          title={interview.report_file_name || t('common.open')}
                        >
                          <FiDownload size={14} /> {t('common.open')}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>-</span>
                      )}
                    </td>}
                    {isVisible('created_by') && <td style={{ fontSize: 13 }}>{interview.created_by_name || '-'}</td>}
                    {isVisible('remarks') && <td
                      style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}
                      title={interview.remarks || ''}
                    >
                      {interview.remarks || '-'}
                    </td>}
                    {isVisible('zoom_duration_minutes') && <td style={{ fontSize: 13 }}>{interview.zoom_duration_minutes != null ? interview.zoom_duration_minutes : '-'}</td>}
                    {isVisible('updated_by_name') && <td style={{ fontSize: 13 }}>{interview.updated_by_name || '-'}</td>}
                    {isVisible('interview_mode') && <td style={{ fontSize: 13 }}>{interview.interview_mode || '-'}</td>}
                    {canRescheduleInterviews && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canUpdateInterviews && interview.outcome === 'pending' && (
                            <div style={actionGroupStyle}>
                              <button
                                className="btn btn-sm btn-success"
                                style={actionBtnStyle('success')}
                                onClick={() => handleMarkOutcome(interview, 'pass')}
                                title={
                                  isFranceCampusName(interview.campus_name)
                                    ? t('interviews.markPassFrance')
                                    : t('interviews.markPass')
                                }
                              >
                                <FiCheck />
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                style={actionBtnStyle('danger')}
                                onClick={() => handleMarkOutcome(interview, 'fail')}
                                title={
                                  isFranceCampusName(interview.campus_name)
                                    ? t('interviews.markFailFrance')
                                    : t('interviews.markFail')
                                }
                              >
                                <FiX />
                              </button>
                              <button
                                className="btn btn-sm btn-warning"
                                style={actionBtnStyle('warning')}
                                onClick={() => handleMarkOutcome(interview, 'student_did_not_join')}
                                title={t('interviews.markDidNotJoin')}
                              >
                                DNJ
                              </button>
                              <button
                                className="btn btn-sm btn-outline"
                                style={actionBtnStyle()}
                                onClick={() => handleReschedule(interview)}
                                title={t('studentDetail.reschedule')}
                              >
                                <FiRefreshCw />
                              </button>
                            </div>
                          )}
                          {!canUpdateInterviews && interview.outcome === 'pending' && (
                            <button
                              className="btn btn-sm btn-outline"
                              style={actionBtnStyle()}
                              onClick={() => handleReschedule(interview)}
                              title={t('studentDetail.reschedule')}
                            >
                              <FiRefreshCw />
                            </button>
                          )}
                          {canUpdateInterviews && (
                            <button
                              className="btn btn-sm btn-outline"
                              style={actionBtnStyle()}
                              onClick={() => handleEdit(interview)}
                              title={t('common.edit')}
                            >
                              <FiEdit />
                            </button>
                          )}
                          {canUpdateInterviews && canDeleteInterviews && (
                            <button
                              className="btn btn-sm btn-outline btn-danger"
                              style={actionBtnStyle('danger')}
                              onClick={() => handleDelete(interview.interview_id)}
                              title={t('common.delete')}
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingInterview(null);
          setModalCampusId(null);
          clearReportFileSelection();
          setForm({
            student_id: '',
            scheduled_at: '',
            timezone: resolveDefaultInterviewTimezone(timezones),
            interview_link: '',
            remarks: ''
          });
        }}
        title={editingInterview ? t('interviews.editTitle') : t('interviews.scheduleTitle')}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('interviews.studentLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select
              value={form.student_id}
              onChange={(e) => setForm({ ...form, student_id: e.target.value })}
              disabled={!!editingInterview}
              required
            >
              <option value="">{t('interviews.selectStudent')}</option>
              {selectableStudents.map(s => (
                <option key={s.student_id} value={s.student_id}>
                  {s.student_ref} - {s.title} {s.first_name} {s.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('interviews.dateLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input
              type="date"
              value={slotDate}
              min={getTodayDateInputMin()}
              onChange={(e) => {
                setSlotDate(e.target.value);
                setSlotId('');
                setDesiredSlotTime('');
              }}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('interviews.slotLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              required
              disabled={!slotDate || slotsLoading}
            >
              <option value="">
                {slotsLoading ? t('studentDetail.modals.loadingSlots') : (slotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
              </option>
              {slotOptions.map((s) => {
                const start = String(s.start_time || '').slice(0, 5);
                const end = String(s.end_time || '').slice(0, 5);
                const label = s.label || `${start} - ${end}`;
                const isFull = !!s.is_full;
                const bypass = canBypassInterviewSlotCapacity(user);
                const disabled = !bypass && isFull;
                const capText = formatInterviewSlotAvailabilitySuffix(s);
                return (
                  <option key={s.slot_id} value={String(s.slot_id)} disabled={disabled}>
                    {label} — {capText}{disabled ? ` ${t('studentDetail.modals.full')}` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label>{t('interviews.timezoneLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              required
            >
              {timezones.length > 0 ? (
                timezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_name}>
                    {tz.timezone_label || tz.timezone_name}
                  </option>
                ))
              ) : (
                <option value={getBrowserIanaTimezone()}>{getBrowserIanaTimezone()} {t('studentDetail.modals.local')}</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label>{t('interviews.interviewLink')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="url"
                value={form.interview_link}
                onChange={(e) => setForm({ ...form, interview_link: e.target.value })}
                placeholder={t('interviews.linkPlaceholder')}
                style={{ flex: 1 }}
              />
              {(form.scheduled_at || (slotDate && desiredSlotTime)) && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={async () => {
                    try {
                      const needsInterviewHost =
                        showInterviewHostColumn &&
                        String(editingInterview?.student_language || '').toUpperCase() === 'FRENCH';
                      const hostUserId = editingInterview?.interview_host_user_id;
                      if (needsInterviewHost && !hostUserId) {
                        toast.error(t('interviews.frenchHostRequired'));
                        return;
                      }
                      const { data } = await zoomAPI.generate({
                        topic: `Interview - ${editingInterview?.first_name || ''}`.trim() || 'Interview',
                        start_time: form.scheduled_at || scheduledAtFromSlot(slotDate, desiredSlotTime),
                        duration: 60,
                        timezone: form.timezone || resolveDefaultInterviewTimezone(timezones),
                        entity_type: editingInterview ? 'interview' : undefined,
                        entity_id: editingInterview?.interview_id || undefined,
                        host_user_id: needsInterviewHost ? hostUserId : undefined
                      });
                      setForm((f) => ({ ...f, interview_link: data?.join_url || f.interview_link }));
                      toast.success(t('interviews.zoomGenerated'));
                    } catch (err) {
                      toast.error(err.response?.data?.message || t('interviews.zoomFailed'));
                    }
                  }}
                >
                  <FiVideo size={14} /> {t('interviews.generateZoom')}
                </button>
              )}
            </div>
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('interviews.remarksLabel')}</label>
            <textarea
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              rows={4}
              placeholder={t('interviews.remarksPlaceholder')}
            />
          </div>

          {editingInterview && editingFranceCampus && (
            <div
              className="form-group"
              style={{
                gridColumn: '1 / -1',
                padding: 12,
                borderRadius: 8,
                background: 'var(--info-light, #eff6ff)',
                border: '1px solid var(--info, #3b82f6)',
                fontSize: 13,
                color: 'var(--text-secondary)'
              }}
            >
              <strong style={{ color: 'var(--text)' }}>{t('interviews.franceCampus')}</strong>
              <p style={{ margin: '8px 0 0' }}>
                {t('interviews.franceInterviewHint')}
              </p>
            </div>
          )}

          {editingInterview?.report_file_path && canPerformAction('interviews', 'read') && (
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>{t('interviews.currentReport')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {editingInterview.report_file_name || t('interviews.uploadedFile')}
                </span>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => openInterviewReport(editingInterview.interview_id)}
                >
                  <FiDownload size={14} /> {t('common.open')}
                </button>
                {canPerformAction('interviews', 'update') && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm btn-danger"
                    disabled={reportBusy}
                    onClick={async () => {
                      if (!window.confirm(t('interviews.removeReportConfirm'))) return;
                      setReportBusy(true);
                      try {
                        await interviewsAPI.deleteReport(editingInterview.interview_id);
                        setEditingInterview((prev) =>
                          prev ? { ...prev, report_file_path: null, report_file_name: null } : null
                        );
                        toast.success(t('interviews.reportRemoved'));
                        loadInterviews();
                      } catch (err) {
                        toast.error(err.response?.data?.message || t('interviews.reportRemoveFailed'));
                      } finally {
                        setReportBusy(false);
                      }
                    }}
                  >
                    <FiTrash2 size={14} /> {t('interviews.removeReport')}
                  </button>
                )}
              </div>
            </div>
          )}

          {editingInterview &&
            !editingFranceCampus &&
            canPerformAction('interviews', 'update') && (
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>{t('interviews.uploadOrReplace')}</label>
              <input
                ref={reportFileInputRef}
                type="file"
                accept={INTERVIEW_REPORT_ACCEPT}
                onChange={(e) => {
                  const inputEl = e.target;
                  const f = inputEl.files?.[0];
                  if (!f) return;
                  const err = getInterviewReportFileError(f);
                  if (err) {
                    toast.error(err);
                    inputEl.value = '';
                    return;
                  }
                  setReportFile(f);
                }}
              />
              {reportFile && (
                <div style={{ marginTop: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span>
                    {t('interviews.selectedFile')} <strong>{reportFile.name}</strong>
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={clearReportFileSelection}
                  >
                    {t('common.clearFile')}
                  </button>
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {t('interviews.reportHint')}
              </p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-outline" onClick={() => {
            setShowModal(false);
            setEditingInterview(null);
            setIsRescheduleMode(false);
            setModalCampusId(null);
            clearReportFileSelection();
            setForm({
              student_id: '',
              scheduled_at: '',
              timezone: resolveDefaultInterviewTimezone(timezones),
              interview_link: '',
              remarks: ''
            });
          }}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            {editingInterview
              ? (isRescheduleMode ? t('interviews.rescheduleBtn') : t('interviews.updateBtn'))
              : t('interviews.scheduleBtn')}
          </button>
        </div>
      </Modal>

      <FranceInterviewOutcomeModal
        open={!!outcomeDialog}
        interview={outcomeDialog?.interview}
        targetOutcome={outcomeDialog?.outcome}
        onClose={() => setOutcomeDialog(null)}
        onSuccess={() => loadInterviews()}
      />
    </div>
  );
};

export default InterviewList;
